import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch,
  FiStar,
  FiZap,
  FiChevronLeft,
  FiRefreshCw,
  FiCopy
} from 'react-icons/fi';
import { useApps } from '../../hooks/useApps';
import { GridSearchBar, GridPagination } from './GridShared';
import type { App, AppTrigger, AppAction } from '../../services/appsService';
import './AppsGrid.css';

// ── App badges (shared between card and detail) ───────────────────────────────

const AppBadges: React.FC<{
  app: Pick<App, 'recommended' | 'active' | 'ai_builder'>;
}> = ({ app }) => (
  <div className='apps-card__badges'>
    {app.recommended && (
      <span className='apps-card__badge apps-card__badge--recommended'>
        <FiStar size={9} /> Recommended
      </span>
    )}
    {app.active && (
      <span className='apps-card__badge apps-card__badge--active'>Active</span>
    )}
    {app.ai_builder && (
      <span className='apps-card__badge apps-card__badge--ai'>
        <FiZap size={9} /> AI Builder
      </span>
    )}
  </div>
);

// ── Static app descriptions (until BE sends description field) ────────────────

const APP_DESCRIPTIONS: Record<string, string> = {
  MAC: 'MAC is a platform that enables agencies to automate workflows and integrate systems through Recipes, triggers, and actions.\n\nThe MAC app in MAC 2.0 Recipes enables secure authentication and provides triggers and actions related to core platform operations, allowing automation of internal processes and system-level interactions within Recipes.',
  AMS: 'AMS is a cloud-based agency management system that helps insurance agencies manage clients, policies, accounting, and daily operations in a centralized platform.\n\nThe AMS app in MAC 2.0 Recipes offers secure authentication along with triggers and actions focused on policy, client, and account management, enabling automation of insurance operations within Recipes.',
  Fenris:
    'The Fenris Data Enrichment Commercial module automatically populates and verifies business details from third-party sources into your AMS, reducing manual data entry.\n\nThe Fenris app in MAC 2.0 Recipes provides secure authentication and supports data enrichment through and actions, allowing Recipes to fetch data and Populate in AMS',
  Rate: 'Get instant quotes from multiple carriers, review policy details, and submit applications-all from one platform.\n\nThe Rate app in MAC 2.0 Recipes includes secure authentication and exposes quote-related triggers and actions, enabling automated rating, quote generation, and quote processing within Recipes.'
};

// ── AppDetail ─────────────────────────────────────────────────────────────────

const AppDetail: React.FC<{ app: App; onBack: () => void }> = ({
  app,
  onBack
}) => {
  const triggers: AppTrigger[] = app.app_triggers ?? [];
  const actions: AppAction[] = app.app_actions ?? [];
  const description = app.description ?? APP_DESCRIPTIONS[app.name] ?? null;

  return (
    <motion.div
      className='apps-detail'
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.button
        className='apps-detail__back'
        onClick={onBack}
        whileHover={{ x: -2 }}
      >
        <FiChevronLeft size={15} /> Back to Apps
      </motion.button>

      {/* Header */}
      <div className='apps-detail__header'>
        <img
          className='apps-detail__header-img'
          src={app.icon?.url}
          alt={app.name}
          onError={e => {
            (e.target as HTMLImageElement).style.opacity = '0.3';
          }}
        />
        <div className='apps-detail__header-info'>
          <h2 className='apps-detail__header-name'>{app.name}</h2>
          <AppBadges app={app} />
          <p className='apps-detail__header-date'>{description}</p>
          {/* {description && <p className='apps-detail__description'>{description}</p>} */}
        </div>
      </div>

      {/* Triggers + Actions */}
      <div className='apps-detail__sections'>
        {/* Triggers */}
        <div className='apps-detail__section'>
          <div className='apps-detail__section-heading'>
            <FiRefreshCw
              size={13}
              className='apps-detail__section-heading-icon--trigger'
            />
            <h3 className='apps-detail__section-title'>Available Triggers</h3>
            <span className='apps-detail__section-count'>
              {triggers.length}
            </span>
          </div>
          <div className='apps-detail__items'>
            {triggers.length === 0 ? (
              <p className='apps-detail__empty'>No triggers available</p>
            ) : (
              triggers.map(t => (
                <div key={t.key} className='apps-detail__item'>
                  <div className='apps-detail__item-bullet apps-detail__item-bullet--trigger'>
                    <FiRefreshCw size={13} />
                  </div>
                  <div className='apps-detail__item-body'>
                    <p className='apps-detail__item-name'>{t.trigger_name}</p>
                    {t.event_types.length > 0 && (
                      <span className='apps-detail__item-meta'>
                        {t.event_types[0]}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Actions */}
        <div className='apps-detail__section'>
          <div className='apps-detail__section-heading'>
            <FiCopy size={13} className='apps-detail__section-heading-icon--action' />
            <h3 className='apps-detail__section-title'>Available Actions</h3>
            <span className='apps-detail__section-count'>{actions.length}</span>
          </div>
          <div className='apps-detail__items'>
            {actions.length === 0 ? (
              <p className='apps-detail__empty'>No actions available</p>
            ) : (
              actions.map(a => (
                <div key={a.key} className='apps-detail__item'>
                  <div className='apps-detail__item-bullet apps-detail__item-bullet--action'>
                    <FiCopy size={12} />
                  </div>
                  <div className='apps-detail__item-body'>
                    <p className='apps-detail__item-name'>{a.name}</p>
                    {/* <span className='apps-detail__item-meta'>
                      <span className='apps-detail__item-method'>{a.request_method.toUpperCase()}</span>
                      {Object.keys(a.form_fields).length} inputs
                    </span> */}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ── AppCard ───────────────────────────────────────────────────────────────────

const AppCard: React.FC<{ app: App; onClick: () => void }> = ({
  app,
  onClick
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      className='apps-card'
      whileHover={{ y: -3, scale: 1.01 }}
      transition={{ duration: 0.18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={onClick}
      style={{
        ...(hovered && { border: '1px solid rgba(34,211,238,0.25)' }),
        boxShadow: hovered ? '0 0 18px rgba(34,211,238,0.08)' : 'none'
      }}
    >
      <div className='apps-card__header'>
        <img
          className='apps-card__icon-img'
          src={app.icon?.url}
          alt={app.name}
          onError={e => {
            (e.target as HTMLImageElement).style.opacity = '0.3';
          }}
        />
        <div className='apps-card__info'>
          <h3 className='apps-card__name'>{app.name}</h3>
          <AppBadges app={app} />
        </div>
      </div>
    </motion.div>
  );
};

// ── AppsGrid (exported) ───────────────────────────────────────────────────────

const AppsGrid: React.FC = () => {
  const {
    apps,
    meta,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    retry,
    goToPage
  } = useApps();

  const [selected, setSelected] = useState<App | null>(null);

  if (selected) {
    return <AppDetail app={selected} onBack={() => setSelected(null)} />;
  }

  const currentPage = meta?.current_page ?? 1;
  const totalPages = meta?.total_pages ?? 1;
  const totalCount = meta?.total_count ?? 0;

  return (
    <div className='apps-grid'>
      <GridSearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder='Search apps...'
      />

      {isLoading ? (
        <div className='mk-loading'>
          <div className='mk-loading__spinner' />
          Loading apps…
        </div>
      ) : error ? (
        <div className='mk-error'>
          <p className='mk-error__msg'>{error}</p>
          <button className='mk-error__retry' onClick={retry}>
            Retry
          </button>
        </div>
      ) : apps.length === 0 ? (
        <div className='mk-empty'>
          <FiSearch size={32} className='apps-grid__empty-icon' />
          <p className='mk-empty__title'>
            {searchQuery ? `No match for "${searchQuery}"` : 'No apps found'}
          </p>
          <p className='mk-empty__sub'>
            {searchQuery
              ? 'Try a different name or clear the search'
              : 'Check back later'}
          </p>
        </div>
      ) : (
        <>
          <div className='apps-grid__cards'>
            {apps.map(app => (
              <AppCard
                key={app.id}
                app={app}
                onClick={() => setSelected(app)}
              />
            ))}
          </div>
          {!searchQuery && (
            <GridPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              label='apps'
              onPage={goToPage}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AppsGrid;
